import { inject, Injectable } from "@angular/core"
import { ApplicationStatus, LoanApplication, OfferType } from "@features/loan-application/loan-application.model"
import { LoanApplicationService } from "@features/loan-application/loan-application.service"
import { catchError, map, of, take } from "rxjs"
import { pendingDecision } from "@features/loan-application/helpers"

export const enum APPLICATION_STATE {
 NEW_APPLICATION = 'new',
 OFFER_INCREASE = 'increase',
 OFFER_DECREASE = 'decrease',
 BANK_STATEMENT_SUBMISSION = 'statement_submit',
 BANK_STATEMENT_MANUAL_UPLOAD = 'statement_upload',
 BANK_STATEMENT_CHECK = 'statement_check',
 PENDING_DECISION = 'decision',
 PENDING_AGREEMENT = 'pending_agreement',
 PENDING_REJECTION = 'pending_rejection',
 AGREEMENT_PROMOTION = 'fast_fee',
}

function getApplicationState(currentApplication: LoanApplication | null): APPLICATION_STATE {
  if (!currentApplication) {
    return APPLICATION_STATE.NEW_APPLICATION
  }

  if (currentApplication.status === ApplicationStatus.APPROVED) {
    return APPLICATION_STATE.PENDING_AGREEMENT
  }

  // current application endpoint returns apps in this state only till throttle period is not expired
  if (currentApplication.status === ApplicationStatus.REJECTED) {
    return APPLICATION_STATE.PENDING_REJECTION
  }

  if (currentApplication.status === ApplicationStatus.BANK_STATEMENT_REQUESTED) {
    return APPLICATION_STATE.BANK_STATEMENT_SUBMISSION
  }

  if (currentApplication.status === ApplicationStatus.BANK_STATEMENT_MANUAL_UPLOAD_REQUESTED) {
    return APPLICATION_STATE.BANK_STATEMENT_MANUAL_UPLOAD
  }

  if (currentApplication.status === ApplicationStatus.BANK_STATEMENT_CHECK_REQUESTED) {
    return APPLICATION_STATE.BANK_STATEMENT_CHECK
  }

  const offerType = currentApplication.offer?.type

  if (currentApplication.status === ApplicationStatus.OFFERED && offerType === OfferType.INCREASE) {
    return APPLICATION_STATE.OFFER_INCREASE
  }

  if (currentApplication.status === ApplicationStatus.OFFERED && offerType === OfferType.DECREASE) {
    return APPLICATION_STATE.OFFER_DECREASE
  }

  if (pendingDecision(currentApplication)) {
    return APPLICATION_STATE.PENDING_DECISION
  }

  return APPLICATION_STATE.NEW_APPLICATION
}

@Injectable({
  providedIn: 'root'
})
export class ApplicationStateStore {
  #applications = inject(LoanApplicationService)

  get() {

    return this.#applications.latest$.pipe(
      take(1),
      map(getApplicationState),
      catchError(() => of(APPLICATION_STATE.NEW_APPLICATION)),
    )
  }
}


